import { useMemo, useRef, useState } from 'react';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';

import { useSelector } from 'react-redux';
import { useGetContactsQuery } from '../../../../../redux/services/contacts/contactsApi';
import { selectUserToken } from '../../../../../redux/features/user/userSelectors';

import { ContactsList } from '../ContactsList';
import { AddContactForm } from '../AddContactForm';
import { SelectInput } from '../../../inputs/SelectInput';
import { Dialog } from '../../../../ui/Dialog';
import { ErrorMsg } from '../../../../ui/ErrorMsg';

import { transformContactsData } from '../../../../../utils/data';
import {
  contactsInputsNames,
  errorMessages,
} from '../../../../../data/constants';

// API select shape {title, value} or a raw value → raw value
const unwrap = field =>
  (typeof field === 'object' && field !== null ? field.value : field) ?? null;

// Realty contacts (ShortContactResource), link objects or legacy plain ids → link objects
export const toContactLinks = (contacts = []) =>
  contacts.map(contact =>
    typeof contact === 'object' && contact !== null
      ? {
          id: Number(contact.id),
          role: unwrap(contact.role),
          relations: unwrap(contact.source ?? contact.relations),
          introducer_type: unwrap(contact.introducer_type),
        }
      : { id: Number(contact), role: null, relations: null, introducer_type: null }
  );

export const ContactsBlockForm = ({ contactsList = [], formProps = {} }) => {
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [editContactId, setEditContactId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const userToken = useSelector(selectUserToken);
  const {
    data: contacts = [],
    isLoading: isContactsLoading,
    error: contactsError,
  } = useGetContactsQuery(
    { token: userToken },
    { skip: !userToken, refetchOnMountOrArgChange: true }
  );

  const links = useMemo(
    () => toContactLinks(formProps?.values?.[contactsInputsNames.contacts]),
    [formProps?.values]
  );

  const selectedIds = useMemo(() => links.map(link => link.id), [links]);

  const ownContactsById = useMemo(
    () => new Map(contacts.map(contact => [contact.id, contact])),
    [contacts]
  );

  // Own pool first (fresh after edits), realty payload as fallback for collaborators' contacts
  const rows = useMemo(() => {
    const realtyContactsById = new Map(
      contactsList.map(contact => [contact.id, contact])
    );

    return links
      .map(link => ({
        link,
        contact: ownContactsById.get(link.id) || realtyContactsById.get(link.id),
      }))
      .filter(row => row.contact);
  }, [links, ownContactsById, contactsList]);

  const editableIds = useMemo(
    () => new Set(ownContactsById.keys()),
    [ownContactsById]
  );

  const options = useMemo(
    () =>
      transformContactsData(
        contacts.filter(contact => !selectedIds.includes(contact.id))
      ),
    [contacts, selectedIds]
  );

  // Undo runs after later edits; read the latest links instead of the removal-time closure
  const linksRef = useRef(links);
  linksRef.current = links;

  const setLinks = newLinks =>
    formProps?.setFieldValue?.(contactsInputsNames.contacts, newLinks);

  const handleAttach = id => {
    if (!selectedIds.includes(Number(id))) {
      setLinks([...links, ...toContactLinks([id])]);
    }
    setExpandedId(Number(id));
  };

  const handleRemove = id => {
    const index = links.findIndex(link => link.id === id);
    const removedLink = links[index];
    const contact = rows.find(row => row.link.id === id)?.contact;

    setLinks(links.filter(link => link.id !== id));

    const name = `${contact?.first_name || ''} ${contact?.last_name || ''}`.trim();

    toast(`${name || 'Contact'} removed`, {
      action: {
        label: 'Undo',
        onClick: () => {
          const current = linksRef.current;
          if (current.some(link => link.id === id)) return;

          const restored = [...current];
          restored.splice(Math.min(index, restored.length), 0, removedLink);
          setLinks(restored);
        },
      },
    });
  };

  const handleToggle = id => setExpandedId(expandedId === id ? null : id);

  const handleLinkChange = (id, name, value) =>
    setLinks(
      links.map(link => (link.id === id ? { ...link, [name]: value } : link))
    );

  return (
    <>
      <div className="flex items-center gap-2 w-full pb-1">
        <SelectInput
          options={options}
          onSelect={([item]) => handleAttach(item.value)}
          placeholder="Search contact"
          isSearchable={true}
          isDisabled={isContactsLoading}
          clearOnSelect={true}
        />

        <button
          type="button"
          title="New contact"
          className="flex flex-none items-center justify-center w-8 h-8 rounded text-[#444] hover:bg-[#f0f1f8] hover:text-[#5b63a8]"
          onClick={() => setIsAddOpen(true)}
        >
          <Plus size={20} />
        </button>
      </div>

      <ContactsList
        rows={rows}
        expandedId={expandedId}
        editableIds={editableIds}
        handleToggle={handleToggle}
        handleEdit={setEditContactId}
        handleRemove={handleRemove}
        handleLinkChange={handleLinkChange}
      />

      {contactsError && <ErrorMsg message={errorMessages.contacts} />}

      {isAddOpen && (
        <Dialog
          content={
            <AddContactForm
              onCreated={handleAttach}
              handleClose={() => setIsAddOpen(false)}
            />
          }
          classes={'max-w-[290px] sm:max-w-[420px]'}
          isOpen={isAddOpen}
          onClose={() => setIsAddOpen(false)}
        />
      )}

      {editContactId && (
        <Dialog
          content={
            <AddContactForm
              isEdit={true}
              canDelete={false}
              contactId={editContactId}
              handleClose={() => setEditContactId(null)}
            />
          }
          classes={'w-[290px] sm:w-[420px]'}
          isOpen={!!editContactId}
          onClose={() => setEditContactId(null)}
        />
      )}
    </>
  );
};
